import axios from "axios";
import { getCookie, deleteCookie } from "./cookie-utils";

const DEFAULT_API_BASE_URL = "";

const sanitizeUrl = (url: string) => url.replace(/\/$/, "");

export const API_BASE_URL = sanitizeUrl(
  process.env.NEXT_PUBLIC_API_BASE_URL ?? DEFAULT_API_BASE_URL
);

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Accept: "application/json",
  },
  timeout: 10000, // 10 segundos timeout
  adapter: ["fetch", "http"], // Prefer fetch to avoid url.parse warning from the http adapter
  proxy: false,
});

// Interceptor para adicionar o token em todas as requisições
api.interceptors.request.use(
  (config) => {
    // Apenas no cliente
    if (typeof window !== "undefined") {
      const token = getCookie("santafe_conecta_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros de autenticação
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Durante build/prerender, silenciar erros de timeout
    if (
      typeof window === "undefined" &&
      (error.code === "ECONNABORTED" ||
        error.message?.includes("timeout") ||
        error.message?.includes("HANGING_PROMISE_REJECTION"))
    ) {
      console.warn(
        "⚠️ API timeout durante build (esperado):",
        error.config?.url
      );
      return Promise.reject(error);
    }

    // Erro de rede (API offline, CORS, etc)
    if (!error.response && typeof window !== "undefined") {
      console.error("Erro de rede - API indisponível:", error.message);
      return Promise.reject({
        ...error,
        isNetworkError: true,
        message:
          "Não foi possível conectar ao servidor. Verifique sua conexão.",
      });
    }

    // Se receber 401, limpar autenticação
    if (error.response?.status === 401 && typeof window !== "undefined") {
      deleteCookie("santafe_conecta_token");
      deleteCookie("santafe_conecta_user");
      // Redirecionar para login se não estiver nessa página
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export const getStorageBaseUrl = () => {
  const fromEnv = process.env.NEXT_PUBLIC_STORAGE_BASE_URL;

  if (fromEnv) {
    return sanitizeUrl(fromEnv);
  }

  try {
    const parsed = new URL(API_BASE_URL);
    return `${parsed.origin}/storage`;
  } catch {
    return "";
  }
};

export const resolveImageUrl = (imagePath?: string | null) => {
  const fallbackImage = "https://placehold.co/800x600?text=Imagem";

  if (!imagePath) {
    return fallbackImage;
  }

  if (/^https?:\/\//i.test(imagePath)) {
    return imagePath;
  }

  const storageBase = getStorageBaseUrl();

  if (!storageBase) {
    return imagePath.startsWith("/") ? imagePath : `/${imagePath}`;
  }

  // Remove leading slashes and /storage/ prefix if present (to avoid duplication)
  const normalizedPath = imagePath
    .replace(/^\/+/, "")
    .replace(/^storage\//, "");
  return `${storageBase}/${normalizedPath}`;
};
